import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { statsApi, projectApi, activityApi, holidaysApi, flagsApi, sectorApi, estoqueApi, osApi, cicloMelhoriaApi, ideiasApi, taskApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { DonutChart, SimpleBarChart, COLORS } from '../components/charts';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Legend,
  LabelList
} from 'recharts';
import { 
  FolderKanban, 
  CheckCircle, 
  Clock,
  ArrowRight,
  Loader2,
  CalendarDays,
  Circle,
  ListTodo,
  User,
  Users,
  PartyPopper,
  Flag,
  Info,
  AlertTriangle,
  RotateCcw,
  Eye,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Layers,
  TrendingUp,
  Calendar as CalendarIcon,
  FileWarning,
  Briefcase,
  Edit3,
  Package,
  Warehouse,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Target,
  Lightbulb,
  ListChecks,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format, isToday, isSameDay, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Helper para normalizar data e evitar problemas de timezone
// Seta a hora para meio-dia para evitar que a data "pule" entre dias
const normalizeDate = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
};

// Helper para obter hoje normalizado
const getTodayNormalized = () => normalizeDate(new Date());

const Dashboard = () => {
  const { user, theme } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [relevantActivities, setRelevantActivities] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [holidayData, setHolidayData] = useState(null);
  const [flaggedItems, setFlaggedItems] = useState(null);
  const [showOtherFlags, setShowOtherFlags] = useState(false);
  const [teamActivity, setTeamActivity] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [monthHolidays, setMonthHolidays] = useState({});
  const [scheduledSectors, setScheduledSectors] = useState([]);
  const [sectorFilter, setSectorFilter] = useState('month'); // 'month', 'quarter', 'all'
  
  // Dados para gráficos operacionais
  const [chartsData, setChartsData] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [cltHumansForChart, setCltHumansForChart] = useState([]);
  const [selectedChartHuman, setSelectedChartHuman] = useState('todos'); // 'todos' ou human_id
  const [cltResumoMes, setCltResumoMes] = useState(null);
  const [loadingResumo, setLoadingResumo] = useState(false);
  
  // Dados de estoque
  const [estoqueStats, setEstoqueStats] = useState({
    totalProdutos: 0,
    alertasBaixoEstoque: 0,
    totalLocais: 0
  });
  
  // Agenda de OSs da semana
  const [weekStart, setWeekStart] = useState(() => startOfWeek(getTodayNormalized(), { weekStartsOn: 1 })); // Segunda
  const [ossSemana, setOssSemana] = useState([]);
  const [loadingOss, setLoadingOss] = useState(false);
  
  // Calendário mensal e dia selecionado
  const [mesAtual, setMesAtual] = useState(new Date());
  const [diaSelecionado, setDiaSelecionado] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [todasOss, setTodasOss] = useState([]);
  
  // Ciclo de Melhoria
  const [cicloMelhoria, setCicloMelhoria] = useState(null);
  const [loadingCiclo, setLoadingCiclo] = useState(false);
  
  // Banco de Ideias
  const [bancoIdeias, setBancoIdeias] = useState(null);
  const [loadingIdeias, setLoadingIdeias] = useState(false);
  
  // Tarefas da semana (para o Gantt)
  const [tarefasSemana, setTarefasSemana] = useState([]);
  
  // Check user permissions - normalize array/object
  const rawPermissions = user?.permissions || [];
  const userPermissions = Array.isArray(rawPermissions) 
    ? rawPermissions 
    : Object.keys(rawPermissions).filter(k => rawPermissions[k]);
  const isAdminOrSuperAdmin = ['admin', 'super_admin', 'diretoria'].includes(user?.role);
  // Permissões operacionais - incluem visualização de OSs e Escalas
  const hasOperationalAccess = isAdminOrSuperAdmin || userPermissions.includes('daily_control');
  const hasReportsAccess = isAdminOrSuperAdmin || userPermissions.includes('reports');
  // Permissões antigas (legado) - não mais usadas mas mantidas por compatibilidade
  const canViewProjects = isAdminOrSuperAdmin || userPermissions.includes('projects');
  const canViewTasks = isAdminOrSuperAdmin || userPermissions.includes('tasks');
  const canViewCalendar = isAdminOrSuperAdmin || userPermissions.includes('calendar');
  // Apenas admin vê estatísticas completas de CLT
  const canViewFullStats = isAdminOrSuperAdmin;

  useEffect(() => {
    loadData();
    loadHolidayData();
    loadFlaggedItems();
    loadChartsData('todos');
    loadCltHumansForChart();
    loadCltResumoMes();
    loadOssSemana();
    loadCicloMelhoria();
    loadBancoIdeias();
    loadTarefasSemana();
  }, []);
  
  // Recarregar OSs quando mudar a semana
  useEffect(() => {
    loadOssSemana();
  }, [weekStart]);
  
  // Recarregar gráfico quando mudar o filtro
  useEffect(() => {
    if (selectedChartHuman) {
      loadChartsData(selectedChartHuman);
    }
  }, [selectedChartHuman]);
  
  // Carregar OSs da semana e do mês
  const loadOssSemana = async () => {
    setLoadingOss(true);
    try {
      const res = await osApi.list();
      const allOss = res.data || [];
      setTodasOss(allOss);
      
      const weekEndDate = endOfWeek(weekStart, { weekStartsOn: 1 });
      
      // Filtrar OSs que tem alguma data dentro da semana
      const ossFiltradas = allOss.filter(os => {
        const dataMontagem = os.data_montagem;
        const dataInicio = os.data_evento;
        const dataFim = os.data_evento_fim || os.prazo_execucao || dataInicio;
        
        // Verificar se qualquer data está dentro da semana
        const weekStartStr = format(weekStart, 'yyyy-MM-dd');
        const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');
        
        const montagemNaSemana = dataMontagem && dataMontagem >= weekStartStr && dataMontagem <= weekEndStr;
        const eventoNaSemana = (dataInicio && dataInicio <= weekEndStr && dataFim >= weekStartStr);
        
        return montagemNaSemana || eventoNaSemana;
      });
      
      setOssSemana(ossFiltradas);
    } catch (error) {
      console.error('Error loading OSs da semana:', error);
      setOssSemana([]);
      setTodasOss([]);
    } finally {
      setLoadingOss(false);
    }
  };
  
  // Carregar Ciclo de Melhoria
  const loadCicloMelhoria = async () => {
    // Disponível para quem tem acesso operacional
    if (!hasOperationalAccess && !isAdminOrSuperAdmin) return;
    setLoadingCiclo(true);
    try {
      const res = await cicloMelhoriaApi.getResumoAtual();
      setCicloMelhoria(res.data);
    } catch (error) {
      console.error('Error loading ciclo de melhoria:', error);
      setCicloMelhoria(null);
    } finally {
      setLoadingCiclo(false);
    }
  };
  
  // Carregar Banco de Ideias
  const loadBancoIdeias = async () => {
    if (!hasOperationalAccess && !isAdminOrSuperAdmin) return;
    setLoadingIdeias(true);
    try {
      const res = await ideiasApi.dashboard();
      setBancoIdeias(res.data);
    } catch (error) {
      console.error('Error loading banco de ideias:', error);
      setBancoIdeias(null);
    } finally {
      setLoadingIdeias(false);
    }
  };
  
  // Carregar tarefas da semana (de todos os projetos)
  const loadTarefasSemana = async () => {
    try {
      // Carregar projetos primeiro
      const projectsRes = await projectApi.list();
      const allProjects = projectsRes.data || [];
      
      // Carregar tarefas de cada projeto
      const allTasks = [];
      for (const project of allProjects) {
        try {
          const tasksRes = await taskApi.list(project.id);
          const tasks = tasksRes.data || [];
          tasks.forEach(task => {
            allTasks.push({ ...task, project_name: project.name, project_id: project.id });
          });
        } catch (e) {
          // Ignorar erro de projeto específico
        }
      }
      
      // Filtrar tarefas que têm prazo na semana atual
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      const weekEndDate = addDays(weekStart, 6);
      const weekEndStr = format(weekEndDate, 'yyyy-MM-dd');
      
      const tarefasFiltradas = allTasks.filter(task => {
        if (!task.due_date) return false;
        const dueDate = task.due_date.split('T')[0];
        return dueDate >= weekStartStr && dueDate <= weekEndStr;
      });
      
      setTarefasSemana(tarefasFiltradas);
    } catch (error) {
      console.error('Error loading tarefas:', error);
      setTarefasSemana([]);
    }
  };
  
  // Filtrar OSs do dia selecionado
  const ossDoDia = todasOss.filter(os => {
    const dataMontagem = os.data_montagem;
    const dataInicio = os.data_evento;
    const dataFim = os.data_evento_fim || os.prazo_execucao || dataInicio;
    
    // Período de montagem: da data_montagem até o dia ANTERIOR ao evento
    const isPeriodoMontagem = dataMontagem && dataInicio && 
      diaSelecionado >= dataMontagem && diaSelecionado < dataInicio;
    
    // Período do evento: do início ao fim
    const isEvento = dataInicio && dataFim && diaSelecionado >= dataInicio && diaSelecionado <= dataFim;
    
    return isPeriodoMontagem || isEvento;
  });
  
  // Verificar se um dia tem OS (para marcar no calendário)
  const getDiaStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    let hasMontagem = false;
    let hasEvento = false;
    
    todasOss.forEach(os => {
      const dataMontagem = os.data_montagem;
      const dataInicio = os.data_evento;
      const dataFim = os.data_evento_fim || os.prazo_execucao || dataInicio;
      
      // Período de montagem: da data_montagem até o dia ANTERIOR ao evento
      if (dataMontagem && dataInicio && dateStr >= dataMontagem && dateStr < dataInicio) {
        hasMontagem = true;
      }
      
      // Período do evento
      if (dataInicio && dataFim && dateStr >= dataInicio && dateStr <= dataFim) {
        hasEvento = true;
      }
    });
    
    return { hasMontagem, hasEvento };
  };
  
  const loadCltHumansForChart = async () => {
    try {
      const res = await statsApi.getChartsData();
      if (res.data?.funcionarios) {
        setCltHumansForChart(res.data.funcionarios);
      }
    } catch (error) {
      console.error('Error loading CLT humans for chart:', error);
    }
  };
  
  const loadChartsData = async (humanFilter = 'todos') => {
    setLoadingCharts(true);
    try {
      const params = humanFilter !== 'todos' ? `?human_id=${humanFilter}` : '';
      const res = await statsApi.getChartsData(humanFilter);
      setChartsData(res.data);
    } catch (error) {
      console.error('Error loading charts data:', error);
    } finally {
      setLoadingCharts(false);
    }
  };
  
  const loadCltResumoMes = async () => {
    setLoadingResumo(true);
    try {
      const res = await statsApi.getCltResumoMes();
      setCltResumoMes(res.data);
    } catch (error) {
      console.error('Error loading CLT resumo mes:', error);
    } finally {
      setLoadingResumo(false);
    }
  };
  
  const loadHolidayData = async () => {
    try {
      // Busca feriados do dia atual
      const currentRes = await holidaysApi.getCurrent();
      // Busca feriados do mês atual para mostrar os próximos
      const now = new Date();
      const monthRes = await holidaysApi.getByMonth(now.getFullYear(), now.getMonth() + 1);
      
      // Filtra apenas feriados futuros do mês (incluindo hoje)
      const today = now.getDate();
      const upcomingHolidays = (monthRes.data.holidays || [])
        .filter(h => h.day >= today)
        .slice(0, 5) // Máximo 5 próximos
        .map(h => ({
          ...h,
          items: h.items.map(item => ({ ...item, date: h.date, day: h.day }))
        }));
      
      setHolidayData({
        current: currentRes.data,
        upcoming: upcomingHolidays,
        hasUpcoming: upcomingHolidays.length > 0
      });
    } catch (error) {
      console.error('Error loading holiday data:', error);
    }
  };

  const loadFlaggedItems = async () => {
    try {
      const res = await flagsApi.getFlaggedItems();
      setFlaggedItems(res.data);
    } catch (error) {
      console.error('Error loading flagged items:', error);
    }
  };

  // Carregar feriados do mês quando o calendário mudar
  const loadMonthHolidays = async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const res = await holidaysApi.getByMonth(year, month);
      
      // Criar um mapa de dia -> tipo de feriado mais importante
      const holidayMap = {};
      (res.data.holidays || []).forEach(h => {
        const types = h.items.map(item => item.type);
        // Prioridade: feriado_nacional > feriado_estadual > feriado_municipal > ponto_facultativo > data_comemorativa
        let priority = 'data_comemorativa';
        if (types.includes('feriado_nacional')) priority = 'feriado_nacional';
        else if (types.includes('feriado_estadual')) priority = 'feriado_estadual';
        else if (types.includes('feriado_municipal')) priority = 'feriado_municipal';
        else if (types.includes('ponto_facultativo')) priority = 'ponto_facultativo';
        
        holidayMap[h.day] = {
          type: priority,
          items: h.items
        };
      });
      
      setMonthHolidays(holidayMap);
    } catch (error) {
      console.error('Error loading month holidays:', error);
    }
  };

  // Carregar feriados quando o mês do calendário mudar
  useEffect(() => {
    loadMonthHolidays(calendarMonth);
  }, [calendarMonth]);
  
  // Carregar tarefas do usuário quando a data muda
  useEffect(() => {
    loadMyTasks();
  }, [selectedDate]);

  const loadData = async () => {
    try {
      const promises = [];
      
      // Carregar projetos que o usuário tem acesso
      if (canViewProjects) {
        promises.push(projectApi.list());
      }
      
      // Admin carrega stats completo
      if (canViewFullStats) {
        promises.push(statsApi.getDashboard());
      }
      
      // Carregar atividades relevantes para o usuário (projetos/tarefas envolvido)
      promises.push(activityApi.listRelevant(15));
      
      // Admin carrega setores com datas para a agenda
      if (isAdminOrSuperAdmin) {
        promises.push(sectorApi.listForCalendar());
      }
      
      const results = await Promise.all(promises);
      
      let idx = 0;
      
      if (canViewProjects) {
        const projectsData = results[idx]?.data || [];
        setProjects(projectsData.slice(0, 5));
        idx++;
      }
      
      if (canViewFullStats) {
        setStats(results[idx]?.data);
        idx++;
      }
      
      // Atividades relevantes
      setRelevantActivities(results[idx]?.data || []);
      idx++;
      
      // Setores com datas (apenas admin)
      if (isAdminOrSuperAdmin) {
        setScheduledSectors(results[idx]?.data || []);
        idx++;
        
        // Carregar atividade do time operacional
        try {
          const teamRes = await statsApi.getTeamActivity();
          setTeamActivity(teamRes.data);
        } catch (err) {
          console.error('Error loading team activity:', err);
        }
        
        // Carregar dados de estoque
        try {
          const [produtosRes, locaisRes, saldosRes] = await Promise.all([
            estoqueApi.listarProdutos(),
            estoqueApi.listarLocais(),
            estoqueApi.listarSaldos()
          ]);
          
          const alertas = saldosRes.data?.filter(s => s.alerta_baixo)?.length || 0;
          setEstoqueStats({
            totalProdutos: produtosRes.data?.length || 0,
            alertasBaixoEstoque: alertas,
            totalLocais: locaisRes.data?.length || 0
          });
        } catch (err) {
          console.error('Error loading estoque stats:', err);
        }
      }
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadMyTasks = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await statsApi.getMyTasks(dateStr);
      setMyTasks(res.data || []);
    } catch (error) {
      console.error('Error loading my tasks:', error);
    }
  };
  
  // Check if date has tasks (for calendar indicator)
  const hasTasksOnDate = (date) => {
    // Simplificado - mostra indicador apenas para data selecionada
    return isSameDay(date, selectedDate) && myTasks.length > 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="company-dashboard" className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header elegante com data, lua e signo */}
      <div 
        className="rounded-xl sm:rounded-2xl p-4 sm:p-6 relative overflow-hidden"
        style={theme ? {
          background: `linear-gradient(135deg, ${theme.primary}08 0%, ${theme.primary_light}05 100%)`,
          borderLeft: `3px solid ${theme.primary}40`
        } : {
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(56, 189, 248, 0.05) 100%)',
          borderLeft: '3px solid rgba(14, 165, 233, 0.4)'
        }}
      >
        <div className="flex flex-col gap-3">
          {/* Data e saudação */}
          <div>
            <p 
              className="text-xs sm:text-sm font-medium uppercase tracking-wider mb-1"
              style={theme ? { color: theme.primary } : { color: '#0ea5e9' }}
            >
              {format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">
              Olá, {user?.name?.split(' ')[0]}! 👋
            </h2>
          </div>
        </div>

        {/* Feriados e Datas Comemorativas - para todos */}
        {(holidayData?.current?.items?.length > 0 || holidayData?.hasUpcoming) && (
          <div className="mt-4 border-t border-slate-100 dark:border-slate-700 pt-4">
            {/* Feriado/evento de HOJE */}
            {holidayData?.current?.items?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">Hoje</p>
                <div className="space-y-2">
                  {holidayData.current.items.map((item, index) => (
                    <div 
                      key={`today-${index}`} 
                      className={`p-3 rounded-lg ${
                        item.type === 'feriado_nacional' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                        item.type === 'feriado_estadual' ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' :
                        item.type === 'feriado_municipal' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                        item.type === 'ponto_facultativo' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' :
                        'bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {item.type === 'feriado_nacional' && <Flag className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                        {item.type === 'feriado_estadual' && <Flag className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />}
                        {item.type === 'feriado_municipal' && <Flag className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />}
                        {item.type === 'ponto_facultativo' && <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />}
                        {item.type === 'data_comemorativa' && <PartyPopper className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <span className={`font-medium ${
                            item.type === 'feriado_nacional' ? 'text-red-800 dark:text-red-300' :
                            item.type === 'feriado_estadual' ? 'text-amber-800 dark:text-amber-300' :
                            item.type === 'feriado_municipal' ? 'text-green-800 dark:text-green-300' :
                            item.type === 'ponto_facultativo' ? 'text-blue-800 dark:text-blue-300' :
                            'text-violet-800 dark:text-violet-300'
                          }`}>
                            {item.name}
                          </span>
                          {item.curiosity && (
                            <p className="text-xs mt-1 text-slate-600 dark:text-slate-400">{item.curiosity}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Agenda de OSs - Visão Mensal + Dia + Semanal */}
      {hasOperationalAccess && (
        <div className="space-y-4">
          {/* Calendário Mensal + Detalhes do Dia */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendário Mensal */}
            <Card className="bg-[#141416] border-zinc-800 rounded-sm lg:col-span-1">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2 text-white tracking-tight">
                    <CalendarIcon className="w-4 h-4 text-cyan-400" />
                    <span className="capitalize">{format(mesAtual, 'MMMM yyyy', { locale: ptBR })}</span>
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setMesAtual(subMonths(mesAtual, 1))}
                      className="h-7 w-7 p-0 hover:bg-zinc-800 text-zinc-400"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setMesAtual(addMonths(mesAtual, 1))}
                      className="h-7 w-7 p-0 hover:bg-zinc-800 text-zinc-400"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Header dias da semana */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                    <div key={d} className="text-center text-[10px] font-mono uppercase tracking-wider text-zinc-500 py-1">{d}</div>
                  ))}
                </div>
                {/* Dias do mês */}
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const monthStart = startOfMonth(mesAtual);
                    const monthEnd = endOfMonth(mesAtual);
                    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
                    const startPadding = getDay(monthStart); // 0 = domingo
                    const paddedDays = [...Array(startPadding).fill(null), ...days];
                    
                    return paddedDays.map((day, idx) => {
                      if (!day) return <div key={`pad-${idx}`} className="h-8" />;
                      
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const { hasMontagem, hasEvento } = getDiaStatus(day);
                      const isSelected = dateStr === diaSelecionado;
                      const isHoje = isToday(day);
                      
                      return (
                        <button
                          key={dateStr}
                          onClick={() => {
                            setDiaSelecionado(dateStr);
                            setWeekStart(startOfWeek(normalizeDate(day), { weekStartsOn: 1 }));
                          }}
                          className={`h-8 rounded-sm text-xs font-medium transition-all relative
                            ${isSelected ? 'bg-cyan-500 text-black font-semibold' : 
                              isHoje ? 'bg-zinc-800 text-white ring-1 ring-cyan-500/50' : 
                              'hover:bg-zinc-800 text-zinc-300'}
                          `}
                        >
                          {format(day, 'd')}
                          {/* Indicadores de OS */}
                          {(hasMontagem || hasEvento) && (
                            <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                              {hasMontagem && <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.6)]" />}
                              {hasEvento && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]" />}
                            </div>
                          )}
                        </button>
                      );
                    });
                  })()}
                </div>
                {/* Legenda */}
                <div className="flex items-center gap-4 mt-3 pt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-amber-400 rounded-full shadow-[0_0_4px_rgba(251,191,36,0.6)]"></div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Montagem</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full shadow-[0_0_4px_rgba(52,211,153,0.6)]"></div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Evento</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Detalhes do Dia Selecionado */}
            <Card className="bg-[#141416] border-zinc-800 rounded-sm lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-white tracking-tight">
                  <CalendarDays className="w-4 h-4 text-cyan-400" />
                  <span className="capitalize">{format(new Date(diaSelecionado + 'T12:00:00'), "EEEE, dd 'de' MMMM", { locale: ptBR })}</span>
                  {ossDoDia.length > 0 && (
                    <Badge className="ml-2 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded-sm font-mono text-[10px]">
                      {ossDoDia.length} OS{ossDoDia.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {loadingOss ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                  </div>
                ) : ossDoDia.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500">
                    <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhuma OS neste dia</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2">
                    {ossDoDia.map(os => {
                      const dataMontagem = os.data_montagem;
                      const dataInicio = os.data_evento;
                      const dataFim = os.data_evento_fim || os.prazo_execucao || dataInicio;
                      
                      // Período de montagem: da data_montagem até o dia ANTERIOR ao evento
                      const isPeriodoMontagem = dataMontagem && dataInicio && 
                        diaSelecionado >= dataMontagem && diaSelecionado < dataInicio;
                      
                      // Período do evento
                      const isEvento = dataInicio && dataFim && diaSelecionado >= dataInicio && diaSelecionado <= dataFim;
                      
                      return (
                        <div 
                          key={os.numero}
                          className={`bg-[#1A1A1E] rounded-sm p-3 border-l-2 transition-all hover:bg-[#222226] ${
                            isPeriodoMontagem ? 'border-l-amber-400' : 'border-l-emerald-400'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono font-semibold text-cyan-400">#{os.numero}</span>
                                {isPeriodoMontagem && (
                                  <Badge className="text-[10px] font-mono bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-sm px-1.5">
                                    <Wrench className="w-3 h-3 mr-1" />
                                    MONTAGEM
                                  </Badge>
                                )}
                                {isEvento && (
                                  <Badge className="text-[10px] font-mono bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-sm px-1.5">
                                    EVENTO
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-zinc-300 truncate">{os.cliente}</p>
                              {os.local && (
                                <p className="text-xs text-zinc-500 truncate mt-0.5">📍 {os.local}</p>
                              )}
                            </div>
                          </div>
                          {/* Equipe */}
                          {os.equipe?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-zinc-800">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Users className="w-3 h-3 text-cyan-400" />
                                {os.equipe.map((m, i) => (
                                  <span key={i} className="text-[10px] font-mono bg-cyan-500/15 text-cyan-300 px-2 py-0.5 rounded-sm border border-cyan-500/20">
                                    {m.nome?.split(' ')[0]}
                                    {m.horario_inicio && ` ${m.horario_inicio}`}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Visão Semanal (Gantt) */}
          <Card className="bg-[#141416] border-zinc-800 rounded-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 text-white tracking-tight">
                  <CalendarDays className="w-4 h-4 text-cyan-400" />
                  Visão Semanal
                  {ossSemana.length > 0 && (
                    <Badge className="ml-2 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 rounded-sm font-mono text-[10px]">
                      {ossSemana.length} OS{ossSemana.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {tarefasSemana.length > 0 && (
                    <Badge className="bg-violet-500/15 text-violet-400 border border-violet-500/30 rounded-sm font-mono text-[10px]">
                      {tarefasSemana.length} Tarefa{tarefasSemana.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setWeekStart(subWeeks(weekStart, 1))}
                    className="h-7 w-7 p-0 dark:hover:bg-slate-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs font-medium dark:text-slate-300 min-w-[150px] text-center">
                    {format(weekStart, "dd/MM", { locale: ptBR })} - {format(endOfWeek(weekStart, { weekStartsOn: 1 }), "dd/MM/yyyy", { locale: ptBR })}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setWeekStart(addWeeks(weekStart, 1))}
                    className="h-7 w-7 p-0 dark:hover:bg-slate-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const today = getTodayNormalized();
                      setWeekStart(startOfWeek(today, { weekStartsOn: 1 }));
                      setDiaSelecionado(format(today, 'yyyy-MM-dd'));
                      setMesAtual(today);
                    }}
                    className="ml-1 text-xs h-7 dark:border-slate-600"
                  >
                    Hoje
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
            {loadingOss ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            ) : (ossSemana.length === 0 && tarefasSemana.length === 0) ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Nenhuma OS ou tarefa nesta semana</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Header com dias da semana */}
                <div className="grid grid-cols-[200px_repeat(7,1fr)] gap-1 mb-2">
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 p-2">OS / Cliente</div>
                  {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                    const day = normalizeDate(addDays(weekStart, dayOffset));
                    const isHoje = isToday(day);
                    return (
                      <div 
                        key={dayOffset} 
                        className={`text-center text-xs font-medium p-2 rounded ${
                          isHoje ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        <div>{format(day, 'EEE', { locale: ptBR })}</div>
                        <div className={isHoje ? 'font-bold' : ''}>{format(day, 'dd/MM')}</div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Linhas das OSs */}
                <div className="space-y-1">
                  {ossSemana.map(os => {
                    const dataMontagem = os.data_montagem;
                    const dataInicio = os.data_evento;
                    const dataFim = os.data_evento_fim || os.prazo_execucao || dataInicio;
                    
                    return (
                      <div 
                        key={os.numero}
                        className="grid grid-cols-[200px_repeat(7,1fr)] gap-1 items-center bg-[#1A1A1E] rounded-sm hover:bg-[#222226] transition-colors"
                      >
                        {/* Info da OS */}
                        <div className="p-2 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-cyan-400 font-mono font-semibold text-sm">#{os.numero}</span>
                          </div>
                          <p className="text-xs text-zinc-300 truncate" title={os.cliente}>
                            {os.cliente?.substring(0, 25) || '-'}
                          </p>
                          {os.local && (
                            <p className="text-[10px] text-zinc-500 truncate" title={os.local}>
                              📍 {os.local?.substring(0, 30)}
                            </p>
                          )}
                          {/* Equipe escalada */}
                          {os.equipe?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Users className="w-3 h-3 text-cyan-400" />
                              <span className="text-[10px] text-cyan-400">
                                {os.equipe.slice(0, 2).map(e => e.nome?.split(' ')[0]).join(', ')}
                                {os.equipe.length > 2 && ` +${os.equipe.length - 2}`}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Células dos dias */}
                        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                          const day = normalizeDate(addDays(weekStart, dayOffset));
                          const dayStr = format(day, 'yyyy-MM-dd');
                          
                          // Período de montagem: da data_montagem até o dia ANTERIOR ao evento
                          const isPeriodoMontagem = dataMontagem && dataInicio && 
                            dayStr >= dataMontagem && dayStr < dataInicio;
                          
                          // Período do evento
                          const isEvento = dataInicio && dataFim && dayStr >= dataInicio && dayStr <= dataFim;
                          const isHoje = isToday(day);
                          
                          let bgClass = '';
                          let content = null;
                          
                          if (isPeriodoMontagem) {
                            const isFirstMont = dayStr === dataMontagem;
                            bgClass = `bg-amber-500 ${isFirstMont ? 'rounded-l-sm' : ''}`;
                            content = (
                              <div className="text-[10px] text-amber-950 font-bold flex flex-col items-center justify-center leading-tight px-1 overflow-hidden">
                                {isFirstMont && (
                                  <span className="flex items-center gap-0.5">
                                    <Wrench className="w-2.5 h-2.5" />
                                    MONT
                                  </span>
                                )}
                                {os.local && <span className="truncate max-w-full text-[9px]">{os.local?.split(' - ')[0]?.substring(0, 18)}</span>}
                                {os.horario_montagem && <span className="text-[9px] opacity-90">{os.horario_montagem}</span>}
                              </div>
                            );
                          } else if (isEvento) {
                            const isFirst = dayStr === dataInicio;
                            const isLast = dayStr === dataFim;
                            bgClass = `bg-emerald-500 ${isFirst ? 'rounded-l-sm' : ''} ${isLast ? 'rounded-r-sm' : ''}`;
                            content = (
                              <div className="text-[10px] text-emerald-950 font-bold flex flex-col items-center justify-center leading-tight px-1 overflow-hidden">
                                {isFirst && <span>INÍCIO</span>}
                                {isLast && dataFim !== dataInicio && <span>FIM</span>}
                                {os.local && <span className="truncate max-w-full text-[9px]">{os.local?.split(' - ')[0]?.substring(0, 18)}</span>}
                                {os.servicos && !isFirst && !isLast && <span className="truncate max-w-full text-[9px]">{os.servicos?.substring(0, 15)}</span>}
                              </div>
                            );
                          }
                          
                          return (
                            <div 
                              key={dayOffset}
                              className={`h-16 rounded-sm flex items-center justify-center ${bgClass} ${
                                isHoje && !bgClass ? 'ring-1 ring-cyan-400/50' : ''
                              }`}
                            >
                              {content}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  
                  {/* Tarefas de Projetos */}
                  {tarefasSemana.map(task => {
                    const dueDate = task.due_date?.split('T')[0];
                    
                    return (
                      <div 
                        key={task.id}
                        className="grid grid-cols-[200px_repeat(7,1fr)] gap-1 items-center bg-[#1A1A1E] rounded-sm hover:bg-[#222226] transition-colors cursor-pointer"
                        onClick={() => navigate(`/projects/${task.project_id}`)}
                      >
                        {/* Info da Tarefa */}
                        <div className="p-2 min-w-0">
                          <div className="flex items-center gap-2">
                            <ListChecks className="w-4 h-4 text-violet-400" />
                            <span className="text-violet-400 font-semibold text-sm truncate">{task.title?.substring(0, 20)}</span>
                          </div>
                          <p className="text-xs text-zinc-400 truncate">
                            📁 {task.project_name?.substring(0, 25) || 'Projeto'}
                          </p>
                          {task.priority && (
                            <Badge className={`text-[10px] mt-1 ${
                              task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                              task.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Média' : 'Normal'}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Células dos dias */}
                        {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                          const day = normalizeDate(addDays(weekStart, dayOffset));
                          const dayStr = format(day, 'yyyy-MM-dd');
                          const isDueDate = dayStr === dueDate;
                          const isHoje = isToday(day);
                          
                          return (
                            <div 
                              key={dayOffset}
                              className={`h-16 rounded-sm flex items-center justify-center ${
                                isDueDate ? 'bg-violet-500' : ''
                              } ${isHoje && !isDueDate ? 'ring-1 ring-cyan-400/50' : ''}`}
                            >
                              {isDueDate && (
                                <div className="text-[10px] text-violet-950 font-bold flex flex-col items-center justify-center leading-tight px-1">
                                  <ListChecks className="w-3 h-3 mb-0.5" />
                                  <span>PRAZO</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                
                {/* Legenda */}
                <div className="flex items-center gap-6 mt-4 pt-3 border-t border-zinc-700">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-amber-500 rounded-sm"></div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Montagem</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-emerald-500 rounded-sm"></div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Evento</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-violet-500 rounded-sm"></div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Tarefa</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Card Ciclo de Melhoria */}
      {hasOperationalAccess && cicloMelhoria && (
        <Card 
          className="bg-gradient-to-r from-[#141416] to-[#1a1a1e] border-cyan-500/30 cursor-pointer hover:border-cyan-400/50 transition-colors"
          onClick={() => navigate('/ciclo-melhoria')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Target className="w-5 h-5 text-cyan-400" />
                Ciclo de Melhoria
                <Badge className={`ml-2 text-xs ${
                  cicloMelhoria.ciclo?.status === 'em_andamento' ? 'bg-amber-500' :
                  cicloMelhoria.ciclo?.status === 'planejamento' ? 'bg-blue-500' :
                  'bg-emerald-500'
                } text-white`}>
                  {cicloMelhoria.ciclo?.status === 'em_andamento' ? 'Em Andamento' :
                   cicloMelhoria.ciclo?.status === 'planejamento' ? 'Planejamento' :
                   'Concluído'}
                </Badge>
              </CardTitle>
              <ArrowRight className="w-5 h-5 text-zinc-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-3">
              <p className="text-zinc-300 font-medium">{cicloMelhoria.ciclo?.titulo}</p>
              {cicloMelhoria.ciclo?.data_inicio && (
                <p className="text-xs text-zinc-500">
                  {format(new Date(cicloMelhoria.ciclo.data_inicio), "dd/MM/yyyy")}
                  {cicloMelhoria.ciclo?.data_fim && ` até ${format(new Date(cicloMelhoria.ciclo.data_fim), "dd/MM/yyyy")}`}
                </p>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-zinc-500">Progresso</span>
                <span className="text-cyan-400 font-mono font-bold">{cicloMelhoria.resumo?.progresso || 0}%</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${cicloMelhoria.resumo?.progresso || 0}%` }}
                />
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{cicloMelhoria.resumo?.total || 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Total</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-slate-400">{cicloMelhoria.resumo?.pendentes || 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Pendentes</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-amber-400">{cicloMelhoria.resumo?.em_andamento || 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Em Andamento</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-400">{cicloMelhoria.resumo?.concluidos || 0}</p>
                <p className="text-[10px] text-zinc-500 uppercase">Concluídos</p>
              </div>
            </div>
            
            {/* Alerta de atrasados */}
            {cicloMelhoria.resumo?.atrasados > 0 && (
              <div className="mt-3 p-2 bg-red-500/10 rounded-lg border border-red-500/30 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs text-red-400">
                  {cicloMelhoria.resumo.atrasados} item(ns) atrasado(s)
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Card Banco de Ideias */}
      {hasOperationalAccess && (
        <Card 
          className="bg-gradient-to-r from-[#141416] to-[#1a1a1e] border-amber-500/30 cursor-pointer hover:border-amber-400/50 transition-colors"
          onClick={() => navigate('/banco-ideias')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <Lightbulb className="w-5 h-5 text-amber-400" />
                Banco de Ideias
              </CardTitle>
              <ArrowRight className="w-5 h-5 text-zinc-500" />
            </div>
          </CardHeader>
          <CardContent>
            {loadingIdeias ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
              </div>
            ) : bancoIdeias && (bancoIdeias.ultimas?.length > 0 || bancoIdeias.total > 0) ? (
              <>
                {/* Stats simplificados */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-white">{bancoIdeias.total || 0}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-amber-400">{bancoIdeias.pendentes || 0}</p>
                    <p className="text-[10px] text-zinc-500 uppercase">Pendentes</p>
                  </div>
                </div>
                
                {/* Últimas ideias */}
                {bancoIdeias.ultimas?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500 uppercase">Últimas ideias</p>
                    {bancoIdeias.ultimas.slice(0, 3).map((ideia, idx) => (
                      <div key={idx} className="p-2 bg-zinc-800/50 rounded-lg">
                        <p className="text-sm text-zinc-300 truncate">{ideia.titulo}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            ideia.tipo === 'sugestao' ? 'bg-amber-500/20 text-amber-400' :
                            ideia.tipo === 'elogio' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {ideia.tipo === 'sugestao' ? 'Sugestão' : ideia.tipo === 'elogio' ? 'Elogio' : 'Reclamação'}
                          </span>
                          <span className="text-[10px] text-zinc-500">{ideia.autor_nome}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <Lightbulb className="w-10 h-10 mx-auto text-amber-400/30 mb-2" />
                <p className="text-sm text-zinc-500">Compartilhe suas ideias!</p>
                <p className="text-xs text-zinc-600">Clique para enviar uma sugestão</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Card Últimos Apontamentos - Destaque no topo */}
      {isAdminOrSuperAdmin && teamActivity?.recent_edits?.length > 0 && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700 border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
              <Edit3 className="w-5 h-5 text-amber-500" />
              Últimos Apontamentos
              <Badge variant="secondary" className="ml-2 text-xs">
                {teamActivity.recent_edits?.length || 0} edições
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {teamActivity.recent_edits?.slice(0, 200).map((edit, idx) => {
                // Converter data de alteração para fuso horário de Manaus (UTC-4)
                const formatManausDateTime = (dateStr) => {
                  if (!dateStr) return { date: '-', time: '-' };
                  try {
                    const date = new Date(dateStr);
                    // Ajustar para Manaus (UTC-4)
                    const manausOffset = -4 * 60 * 60 * 1000;
                    const manausDate = new Date(date.getTime() + manausOffset);
                    const dia = String(manausDate.getUTCDate()).padStart(2, '0');
                    const mes = String(manausDate.getUTCMonth() + 1).padStart(2, '0');
                    const ano = manausDate.getUTCFullYear();
                    const hora = String(manausDate.getUTCHours()).padStart(2, '0');
                    const min = String(manausDate.getUTCMinutes()).padStart(2, '0');
                    const seg = String(manausDate.getUTCSeconds()).padStart(2, '0');
                    return {
                      date: `${dia}/${mes}/${ano}`,
                      time: `${hora}:${min}:${seg}`,
                      full: `${dia}/${mes}/${ano} ${hora}:${min}:${seg}`
                    };
                  } catch {
                    return { date: '-', time: '-' };
                  }
                };

                // Formatar data de referência (qual dia foi editado)
                const formatRefDate = (dateStr) => {
                  if (!dateStr) return '-';
                  try {
                    return format(new Date(dateStr + 'T12:00:00'), "dd/MM (EEE)", { locale: ptBR });
                  } catch {
                    return dateStr;
                  }
                };

                const alteredAt = formatManausDateTime(edit.updated_at);
                const refDate = formatRefDate(edit.date);

                return (
                  <div 
                    key={idx}
                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Lado Esquerdo: Quando foi alterado */}
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 text-center bg-amber-100 dark:bg-amber-900/30 rounded-lg px-2 py-1 min-w-[90px]">
                          <p className="text-xs font-bold text-amber-700 dark:text-amber-300">{alteredAt.date}</p>
                          <p className="text-[10px] text-amber-600 dark:text-amber-400">{alteredAt.time}</p>
                          <p className="text-[9px] text-amber-500">(Manaus)</p>
                        </div>
                        
                        {/* Info do que foi alterado */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                            {edit.human_name || 'N/A'}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            Dia alterado: <span className="font-medium text-blue-600 dark:text-blue-400">{refDate}</span>
                          </p>
                          {edit.changes?.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1" data-testid="edit-changes-list">
                              {edit.changes.slice(0, 8).map((change, i) => (
                                <span
                                  key={i}
                                  className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                >
                                  {change}
                                </span>
                              ))}
                              {edit.changes.length > 8 && (
                                <span className="text-[10px] text-slate-400">+{edit.changes.length - 8} alterações</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Lado Direito: Quem alterou */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-500 dark:text-slate-400">por</p>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{edit.updated_by || 'Sistema'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Atalhos Fechamento CLT - para admin */}
      {isAdminOrSuperAdmin && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">Fechamento CLT</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Relatórios para conferência de ponto do mês</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/folha-clt')}
                  className="flex items-center gap-2 border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                >
                  <ListTodo className="w-4 h-4" />
                  Ponto CLT com Obs.
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
